// src/features/admin/layout/AdminLayout.tsx
// چیدمان پنل مدیریت — سایدبار گروه‌بندی‌شده طبق سند طراحی (بخش ۵.۱)
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppSelector, useAppDispatch } from '@app/store/hooks';
import { logout } from '@app/store/slices/authSlice';
import { clearUser } from '@app/store/slices/userSlice';
import { usePermissions } from '@core/hooks/usePermissions';
import { getPersianDate } from '@core/utils/dateUtils';
import { getRoleDisplayLabels } from '@core/constants/roleLabels';

interface MenuItem {
  label: string;
  to: string;
}

interface MenuGroup {
  title?: string;
  items: MenuItem[];
}

// ── ساختار منو مطابق سند طراحی (بخش ۵.۱) ───────────────────────
const ALL_GROUPS: MenuGroup[] = [
  { items: [{ label: 'داشبورد', to: '/admin/dashboard' }] },
  {
    title: 'مدیریت کاربران و دسترسی',
    items: [
      { label: 'کاربران', to: '/admin/users' },
      { label: 'ادمین‌ها', to: '/admin/admins' },
      { label: 'نقش‌ها', to: '/admin/roles' },
      { label: 'دسترسی‌ها', to: '/admin/permissions' },
    ],
  },
  {
    title: 'سازمان',
    items: [
      { label: 'کارتخوان‌ها', to: '/admin/pos-devices' },
    ],
  },
  {
    title: 'پشتیبانی و سیستم',
    items: [
      { label: 'تیکت‌ها', to: '/admin/tickets' },
      { label: 'تنظیمات سیستم', to: '/admin/settings' },
      { label: 'لاگ‌ها (Audit)', to: '/admin/logs' },
    ],
  },
];

const pageTitleMap: Record<string, string> = {
  '/admin/dashboard': 'داشبورد مدیریت سامانه بارنامه گرام',
  '/admin/pos-devices': 'مدیریت کارتخوان‌ها',
  '/admin/users': 'مدیریت کاربران',
  '/admin/admins': 'مدیریت ادمین‌ها',
  '/admin/roles': 'مدیریت نقش‌ها',
  '/admin/permissions': 'مدیریت دسترسی‌ها',
  '/admin/settings': 'تنظیمات سیستم',
  '/admin/logs': 'لاگ‌ها و گزارش‌های سیستمی',
};

const ItemIcon: React.FC<{ label: string; active?: boolean }> = ({ label, active = false }) => {
  const common = { className: 'h-4 w-4', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8 };
  let path = 'M4 6h16M4 12h16M4 18h16';
  if (label === 'داشبورد') path = 'M4 13h6V4H4v9Zm10 7h6v-9h-6v9ZM4 20h6v-3H4v3Zm10-12h6V4h-6v4Z';
  if (label === 'کاربران' || label === 'ادمین‌ها') path = 'M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20m6-8a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm5-6.5a3 3 0 0 1 0 5.8M16 15.2a3.5 3.5 0 0 1 4 3.3V20';
  if (label === 'نقش‌ها') path = 'M12 3 4.5 6v5c0 4.6 3.1 8.2 7.5 10 4.4-1.8 7.5-5.4 7.5-10V6L12 3Zm0 4v5m-2.5-2.5h5';
  if (label === 'دسترسی‌ها') path = 'm15.5 8.5 2-2a3 3 0 1 0-4.2-4.2l-2 2a3 3 0 0 0 0 4.2 3 3 0 0 0 4.2 0Zm-7 7-2 2a3 3 0 1 0 4.2 4.2l2-2a3 3 0 0 0 0-4.2 3 3 0 0 0-4.2 0Zm-1.5-5 10 2';
  if (label === 'کارتخوان‌ها') path = 'M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm2 4h8M8 11h2m2 0h2m2 0h.01M8 15h2m2 0h2m2 0h.01M8 18h8';
  if (label === 'تیکت‌ها') path = 'M5 5.5A2.5 2.5 0 0 1 7.5 3h9A2.5 2.5 0 0 1 19 5.5v6a2.5 2.5 0 0 1-2.5 2.5H12l-4 3v-3H7.5A2.5 2.5 0 0 1 5 11.5v-6Z';
  if (label === 'تنظیمات سیستم') path = 'M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Zm0-5v2m0 13v2m9-8h-2M5 12H3m15.4-6.4-1.4 1.4M7 17l-1.4 1.4m12.8 0L17 17M7 7 5.6 5.6';
  if (label === 'لاگ‌ها (Audit)') path = 'M6 4h12v16H6V4Zm3 4h6m-6 4h6m-6 4h4';
  return <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${active ? 'bg-white/15 text-white' : 'bg-slate-800/80 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-200'}`}><svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d={path} /></svg></span>;
};

const normalizeAdminProfileValue = (value: string): string => {
  const normalized = value.trim().replace(/\s+/g, ' ');
  return /^(super\s+admin|سوپر\s*ادمین|ادمین\s*کل)$/i.test(normalized) ? 'ادمین' : normalized;
};

const AdminLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company } = useAppSelector((state) => state.user);
  const { roles, roleTitles } = usePermissions();
  const [profilePreviewOpen, setProfilePreviewOpen] = useState(false);
  const profilePreviewRef = useRef<HTMLDivElement>(null);
  const isGlobalAdmin = roles.some((role) => role === 'admin')
    && !user?.company_id
    && !localStorage.getItem('company_id');
  const displayRoleLabels = getRoleDisplayLabels(roles, roleTitles).map(normalizeAdminProfileValue);

  useEffect(() => {
    if (!profilePreviewOpen) return;

    const handleDocumentClick = (event: MouseEvent) => {
      if (!profilePreviewRef.current?.contains(event.target as Node)) {
        setProfilePreviewOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setProfilePreviewOpen(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [profilePreviewOpen]);

  const groups = ALL_GROUPS.filter((group) => group.items.length > 0);

  const currentTitle = pageTitleMap[location.pathname] || 'پنل مدیریت';
  const rawName = normalizeAdminProfileValue(
    [user?.name, user?.lastname].filter(Boolean).join(' ').trim() ||
      localStorage.getItem('name') ||
      'کاربر',
  );
  const mobileNumber = user?.mobile_number || localStorage.getItem('mobile') || 'ثبت نشده';
  const companyName = user?.company?.name || company?.name || '';
  const accountId = user?.id ? String(user.id) : 'ثبت نشده';

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearUser());
    navigate('/Adminlogin_pass');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex" dir="rtl">
      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside className="w-[18rem] shrink-0 bg-[#0d172a] text-slate-100 flex flex-col sticky top-0 h-screen overflow-hidden">
        <div className="border-b border-white/10 px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-lg font-black text-white shadow-lg shadow-blue-950/30">
              پ
            </div>
            <div className="min-w-0">
              <div className="truncate text-[15px] font-black leading-tight text-white">پنل مدیریت</div>
              <div className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-slate-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> ادمین سامانه</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {groups.map((group, groupIndex) => (
            <div key={groupIndex} className={groupIndex > 0 ? 'mt-6' : ''}>
              {group.title && (
                <div className="mb-2 flex items-center gap-2 px-3 text-[10px] font-bold tracking-wide text-slate-500">
                  <span className="h-1 w-1 rounded-full bg-slate-600" />
                  <span>{group.title}</span>
                </div>
              )}
              <div className="space-y-1.5">
                {group.items.map((item) =>
                  item.to.startsWith('/admin') ? (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      end={item.to === '/admin/dashboard'}
                      className="group"
                    >
                      {({ isActive }) => (
                        <div className={`flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-bold transition-all ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-950/30' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'}`}>
                          <ItemIcon label={item.label} active={isActive} />
                          <span className="flex-1">{item.label}</span>
                          <svg className={`h-3.5 w-3.5 shrink-0 transition-transform ${isActive ? 'text-white/80' : 'text-slate-600 group-hover:text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m15 5-7 7 7 7" /></svg>
                        </div>
                      )}
                    </NavLink>
                  ) : (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-bold text-slate-300 transition-all hover:bg-white/[0.06] hover:text-white"
                    >
                      <ItemIcon label={item.label} />
                      <span className="flex-1">{item.label}</span>
                      <svg className="h-3.5 w-3.5 shrink-0 text-slate-600 transition-colors group-hover:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m15 5-7 7 7 7" /></svg>
                    </Link>
                  )
                )}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 p-3">
          <button
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-800/90 px-4 py-3 text-sm font-bold text-slate-300 transition-colors hover:bg-red-600 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            خروج از پنل
          </button>
        </div>
      </aside>
      {/* ── Main ────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-20 px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{currentTitle}</h1>
            <p className="text-xs text-gray-500 mt-0.5">{getPersianDate(new Date())}</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative" ref={profilePreviewRef}>
              <button
                type="button"
                aria-expanded={profilePreviewOpen}
                aria-controls="admin-profile-preview"
                onClick={() => setProfilePreviewOpen((open) => !open)}
                className="flex items-center gap-3 rounded-xl px-2 py-1.5 text-right transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                  {rawName.charAt(0)}
                </div>
                <div className="hidden md:block">
                  <div className="text-sm font-semibold text-gray-900">{rawName}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    {displayRoleLabels.slice(0, 2).map((label, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-medium rounded-full"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${profilePreviewOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
                </svg>
              </button>

              {profilePreviewOpen && (
                <div
                  id="admin-profile-preview"
                  role="dialog"
                  aria-label="مشخصات حساب"
                  className="absolute left-0 right-auto top-[calc(100%+0.75rem)] z-50 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white text-right shadow-xl"
                >
                  <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-700">
                      {rawName.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-slate-900">{rawName}</div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {(displayRoleLabels.length > 0 ? displayRoleLabels.slice(0, 2) : ['ثبت نشده']).map((label, index) => (
                          <span key={index} className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2.5 px-4 py-3 text-sm">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-500">شماره تلفن</span>
                      <span dir="ltr" className="font-semibold text-slate-800">{mobileNumber}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-slate-500">شناسه حساب</span>
                      <span dir="ltr" className="font-semibold text-slate-800">{accountId}</span>
                    </div>
                    {companyName && (
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-slate-500">شرکت</span>
                        <span className="max-w-[11rem] truncate font-semibold text-slate-800">{companyName}</span>
                      </div>
                    )}
                    <div className="border-t border-slate-100 pt-2.5">
                      <div className="mb-1.5 text-xs text-slate-500">نقش‌های حساب</div>
                      <div className="flex flex-wrap gap-1.5">
                        {(displayRoleLabels.length > 0 ? displayRoleLabels : ['ثبت نشده']).map((label, index) => (
                          <span key={index} className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700">
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!isGlobalAdmin && (
              <Link
                to="/dashboard"
                className="hidden lg:flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                بازگشت به سامانه
              </Link>
            )}
          </div>
        </header>

        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="p-6"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
};

export default AdminLayout;
